<template>
  <component-wrapper v-if="mountedNonDialog">
    <forgot-password-fi
      v-if="isAppView"
      class="site-forgot-password--app"
    />
    <div
      v-else
      class="site-forgot-password"
    >
      <transition
        enter-active-class="animated fadeIn"
        leave-active-class="animated fadeOut"
        mode="out-in"
      >
        <grid-container-fi
          v-if="!isSendEmail"
          :key="'container-form'"
        >
          <div
            class="fi-col-start-1 fi-col-end-13 md:fi-col-start-3 md:fi-col-end-11 lg:fi-col-start-4 lg:fi-col-end-10"
          >
            <h1
              v-if="!isEditMode && fields.headline?.value"
              class="site-forgot-password__headline"
            >
              {{ fields.headline.value }}
            </h1>

            <!-- eslint-disable vue/no-v-html -->
            <div
              v-if="isEditMode && fields.headline?.editable"
              class="site-forgot-password__headline"
              v-html="fields.headline.editable"
            />
            <!-- eslint-enable vue/no-v-html -->

            <richtext-fi
              class="site-forgot-password__text"
              :html-content="fields.description"
            />
          </div>

          <div
            class="fi-col-start-1 fi-col-end-13 md:fi-col-start-3 md:fi-col-end-7 lg:fi-col-start-4 lg:fi-col-end-7 xxl:fi-col-start-5 xxl:fi-col-end-8"
          >
            <div class="site-forgot-password__form">
              <div class="site-forgot-password__input">
                <text-input-fi
                  id="email"
                  ref="email"
                  v-model.trim="form.email"
                  name="email"
                  :rules="emailRules"
                  :maxlength="120"
                  :label="fields.emailFieldLabel?.value"
                  required
                  large-from="md"
                  @validation-changed="handleValidationChanged"
                  @keyup="validateCharacters(form.email, 'email')"
                  @blur="enableEmailValidation = true"
                />
              </div>

              <loading-indicator-wrapper
                :loading="isLoading || false"
                dark
                variant="spinnerLocal"
                spinoff="in-relative-centered"
                class="site-forgot-password__spinner"
              >
                <button-fi
                  :class="{
                    'button-fi__primary button-fi--stretched': true,
                    'button-fi--large-from-sm': true,
                  }"
                  :disabled="!form.emailValid"
                  data-test="request-password-button"
                  @click="getNewPassword"
                >
                  {{ fields.buttonText?.value }}
                </button-fi>
              </loading-indicator-wrapper>
            </div>
          </div>
        </grid-container-fi>
        <grid-container-fi
          v-else
          :key="'container-result-status'"
          class="site-forgot-password__result-status"
        >
          <div
            class="fi-col-start-1 fi-col-end-13 md:fi-col-start-2 md:fi-col-end-12 lg:fi-col-start-4 lg:fi-col-end-10"
          >
            <result-status-fi :headline="fields.successMessageBox">
              <richtext-fi :html-content="fields.successDescription" />
            </result-status-fi>
          </div>
        </grid-container-fi>
      </transition>
    </div>
  </component-wrapper>
</template>

<script setup lang="ts">
import { useClientHandle } from '@urql/vue';
import { ButtonFi, LoadingIndicatorWrapper, RichtextFi, TextInputFi } from 'atoms';
import { computed, onMounted, reactive, ref } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import ForgotPasswordFi from '@/components/forgot-password-fi/ForgotPasswordFi.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import ResultStatusFi from '@/components/result-status-fi/ResultStatusFi.vue';
import { useFields } from '@/composables/fields';
import { useFischer } from '@/composables/fischerPlugin';
import { useValidators } from '@/composables/validators';
import type {
  RequestForgottenPasswordMutation,
  RequestForgottenPasswordMutationVariables,
} from '@/generated/graphql';
import MYACCOUNTREQUESTFORGOTTENPASSWORD from '@/graphql/myAccountRequestForgottenPassword.gql';
import type { FischerPlugin } from '@/lib/fischerPlugin';
import { useNotificationStore } from '@/store/pinia/notification';

defineOptions({ name: 'ForgotPassword' });

const fields = useFields('MyAccountForgotPasswordFields');

const notificationStore = useNotificationStore();
const fischer = useFischer();
const handle = useClientHandle();
const { rules, hasInvalidCharacters, validateCharacters } = useValidators();

const form = reactive({
  email: '',
  emailValid: false,
});

const isSendEmail = ref(false);
const isLoading = ref(false);
const enableEmailValidation = ref(false);
const mountedNonDialog = ref(false);

const emailRules = computed(() =>
  hasInvalidCharacters.value || enableEmailValidation.value ? rules.value.email : [],
);

const handleValidationChanged = (value: any) => {
  form.emailValid = Boolean(value);
};

const getNewPassword = async () => {
  if (!form.email || !form.emailValid) return;

  isLoading.value = true;
  try {
    const { error: pwError } = await handle.client
      .mutation<RequestForgottenPasswordMutation, RequestForgottenPasswordMutationVariables>(
        MYACCOUNTREQUESTFORGOTTENPASSWORD,
        {
          userId: form.email,
          baseSiteId: fischer.baseSiteId(),
          host: fischer.hostname(),
          lang: fischer.language_639_1(),
        },
      )
      .toPromise();
    if (pwError) {
      console.error(pwError);
      notificationStore.openGeneralErrorNotification();
    } else {
      isSendEmail.value = true;
    }
  } catch (error) {
    console.error(error);
    notificationStore.openGeneralErrorNotification();
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  if (
    fischer.isLoginDialogEnabled() &&
    !fischer.app.isAppView() &&
    (!IS_SHOWROOM || window.confirm('redirect to password dialog?'))
  ) {
    const page: keyof FischerPlugin['pages'] = 'passwordForgotten';
    window.location.href = `${fischer.hostUrlBase()}/${fischer.siteLanguage().toLowerCase()}#${page}-dialog`;
  } else {
    mountedNonDialog.value = true;
  }
});
</script>

<style scoped src="./my-account-forgot-password" />
